import React, { useState, useEffect } from 'react';
import { Loader2 } from 'lucide-react';
import { useIsMutating } from '@tanstack/react-query';

const Button = ({ children, onClick, displayType, type, disabled, customStyle, isLoading }) => {
  const [internalLoading, setInternalLoading] = useState(false);
  const [autoLoading, setAutoLoading] = useState(false);
  const [wasClicked, setWasClicked] = useState(false);
  const isMutating = useIsMutating();
  const prevIsMutating = React.useRef(isMutating);

  useEffect(() => {
    // If mutations increased and this button was recently clicked, it likely triggered the mutation
    if (isMutating > prevIsMutating.current && wasClicked) {
      setAutoLoading(true);
      setWasClicked(false);
    }
    // If all mutations are done, stop loading
    if (isMutating === 0) {
      setAutoLoading(false);
    }
    prevIsMutating.current = isMutating;
  }, [isMutating, wasClicked]);

  const handleClick = async (e) => {
    // Mark as clicked so we can claim the next mutation start
    setWasClicked(true);
    // Timeout to reset wasClicked if no mutation starts (e.g. validaton error)
    setTimeout(() => setWasClicked(false), 1000);

    if (onClick) {
      const result = onClick(e);
      if (result instanceof Promise) {
        setInternalLoading(true);
        try {
          await result;
        } finally {
          setInternalLoading(false);
        }
      }
    }
  };

  // We detect loading state for submit and regular buttons by checking global mutation state.
  // This ensures buttons show loading even if the parent form resets 'disabled' state too early.
  // We include type === 'button' to cover actions like Delete/Duplicate that are not submit buttons but trigger mutations.
  const isBusy = isLoading || internalLoading || autoLoading;

  if (displayType === 'downloadButton') {
    return (
      <button
        type={type}
        disabled={disabled || isBusy}
        className={`border ${displayType === 'secondary' ? 'border-customBlue' : 'border-customBlue bg-customBlue'} text-white  py-2 px-4 rounded-lg focus:outline-none focus:shadow-outline flex justify-center items-center`}
        style={customStyle}
        onClick={handleClick}>
        {isBusy ? <Loader2 className="animate-spin h-5 w-5" /> : children}
      </button>
    );
  }
  if (displayType === 'primary-red') {
    return (
      <button
        type={type}
        className={`bg-[#C2281B] py-2 px-4 rounded-lg focus:outline-none focus:shadow-outline flex justify-center items-center`}
        disabled={disabled || isBusy}
        onClick={handleClick}>
        <p className={`text-stone-100 hover:customBlue`}>
          {isBusy ? <Loader2 className="animate-spin h-5 w-5" /> : children}
        </p>
      </button>
    );
  }

  if (displayType === 'full-w-primary') {
    return (
      <button
        type={type}
        className={`border ${displayType === 'secondary' ? 'border-customBlue' : 'border-customBlue bg-customBlue'}  py-2 px-4 rounded-lg focus:outline-none focus:shadow-outline w-full flex justify-center items-center`}
        disabled={disabled || isBusy}
        onClick={handleClick}>
        <p className={`text-stone-100 hover:customBlue`}>
          {isBusy ? <Loader2 className="animate-spin h-5 w-5" /> : children}
        </p>
      </button>
    );
  }

  if (displayType === 'full-w-secondary') {
    return (
      <button
        type={type}
        className={`border  border-customBlue text-customBlue py-2 px-4 rounded-lg focus:outline-none focus:shadow-outline w-full flex justify-center items-center`}
        disabled={disabled || isBusy}
        onClick={handleClick}>
        <p className={`hover:customBlue`}>
          {isBusy ? <Loader2 className="animate-spin h-5 w-5" /> : children}
        </p>
      </button>
    );
  }

  if (displayType === 'secondary-red') {
    return (
      <button
        type={type}
        className={`border border-[#C2281B]  py-2 px-4 rounded-lg focus:outline-none focus:shadow-outline flex justify-center items-center`}
        disabled={disabled || isBusy}
        onClick={handleClick}>
        <p className={`text-[#C2281B] hover:customBlue`}>
          {isBusy ? <Loader2 className="animate-spin h-5 w-5" /> : children}
        </p>
      </button>
    );
  }

  return (
    <button
      type={type}
      disabled={disabled || isBusy}
      className={`border ${displayType === 'secondary' ? 'border-customBlue' : 'border-customBlue bg-customBlue'}  py-2 px-4 rounded-lg focus:outline-none focus:shadow-outline flex justify-center items-center`}
      style={customStyle}
      onClick={handleClick}>
      <p
        className={`${displayType === 'secondary' && '!text-customBlue'} text-white hover:customBlue`}>
        {isBusy ? <Loader2 className="animate-spin h-5 w-5" /> : children}
      </p>
    </button>
  );
};

export default Button;
